// src/pages/WeeklyTimesheet.tsx
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import axiosInstance from "@/api/axiosConfig";
import { useToast } from "@/hooks/use-toast";
import { format, startOfWeek, addDays, subWeeks, addWeeks, isFuture, isToday, endOfWeek } from "date-fns";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface Timesheet {
  timesheetId: number | null;  // Update to allow null
  userEmail: string;
  weekStartDate: string;
  monday: number | null;
  tuesday: number | null;
  wednesday: number | null;
  thursday: number | null;
  friday: number | null;
  saturday: number | null;
  sunday: number | null;
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  comments: string | null;
}

const WeeklyTimesheet = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState<Date>(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [timesheet, setTimesheet] = useState<Timesheet | null>(null);
  const [comments, setComments] = useState<string>("");

  const MIN_DATE = new Date(2025, 0, 1); // January 1, 2025

  const loadTimesheet = async () => {
    try {
      setIsLoading(true);
      const weekStart = format(currentWeek, 'yyyy-MM-dd');
      const response = await axiosInstance.get(`/api/v1/week/${weekStart}`);
      
      if (response.data) {
        setTimesheet(response.data);
        setComments(response.data.comments || "");
      } else {
        // Initialize new timesheet with proper typing
        const newTimesheet: Timesheet = {
          timesheetId: null,
          userEmail: user?.email || "",
          weekStartDate: weekStart,
          monday: 0,
          tuesday: 0,
          wednesday: 0,
          thursday: 0,
          friday: 0,
          saturday: 0,
          sunday: 0,
          status: 'draft',
          comments: ""
        };
        setTimesheet(newTimesheet);
        setComments("");
      }
    } catch (error) {
      console.error("Failed to load timesheet:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load timesheet",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTimesheet();
  }, [currentWeek]);

  const canEdit = !timesheet?.status || timesheet.status === 'draft';

  const isWeekInFuture = (date: Date) => {
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
    return isFuture(weekStart) && !isToday(weekStart);
  };

  const isDateDisabled = (date: Date) => {
    return date < MIN_DATE || isWeekInFuture(date);
  };

  const handleHourChange = (day: keyof Timesheet, value: string) => {
    if (!canEdit || isWeekInFuture(currentWeek)) return;
    
    let numValue: number | null = null;
    
    if (value === "") {
      numValue = null;
    } else {
      // Remove leading zeros and parse
      const cleanValue = value.replace(/^0+(?=\d)/, '');
      numValue = parseFloat(cleanValue);
    }

    if (numValue === null || (!isNaN(numValue) && numValue >= 0 && numValue <= 24)) {
      setTimesheet(prev => {
        if (!prev) {
          return {
            timesheetId: null,
            userEmail: user?.email || "",
            weekStartDate: format(currentWeek, 'yyyy-MM-dd'),
            monday: day === 'monday' ? numValue : 0,
            tuesday: day === 'tuesday' ? numValue : 0,
            wednesday: day === 'wednesday' ? numValue : 0,
            thursday: day === 'thursday' ? numValue : 0,
            friday: day === 'friday' ? numValue : 0,
            saturday: day === 'saturday' ? numValue : 0,
            sunday: day === 'sunday' ? numValue : 0,
            status: 'draft',
            comments: ""
          };
        }
        return {
          ...prev,
          [day]: numValue
        };
      });
    }
  };

  const calculateTotal = () => {
    if (!timesheet) return 0;
    const hours = [
      timesheet.monday,
      timesheet.tuesday,
      timesheet.wednesday,
      timesheet.thursday,
      timesheet.friday,
      timesheet.saturday,
      timesheet.sunday
    ];
    return hours.reduce((sum: number, hour) => sum + (hour || 0), 0);
  };

  const handleSave = async (isDraft: boolean = true) => {
    if (!timesheet) return;

    try {
      // Base data that's always sent
      const baseData = {
        userEmail: timesheet.userEmail,               // string (user's email)
        weekStartDate: timesheet.weekStartDate,       // string (yyyy-MM-dd format)
        monday: timesheet.monday,                     // number | null (hours)
        tuesday: timesheet.tuesday,                   // number | null (hours)
        wednesday: timesheet.wednesday,               // number | null (hours)
        thursday: timesheet.thursday,                 // number | null (hours)
        friday: timesheet.friday,                     // number | null (hours)
        saturday: timesheet.saturday,                 // number | null (hours)
        sunday: timesheet.sunday,                     // number | null (hours)
        comments: comments,                           // string (from textarea)
        status: isDraft ? 'draft' : 'submitted'      // string ('draft' or 'submitted')
      };

      // Add timesheetId only for PUT requests (updating existing timesheet)
      const data = timesheet.timesheetId 
        ? { ...baseData, timesheetId: timesheet.timesheetId }
        : baseData;

      // Use axiosInstance and remove trailing slash from endpoint
      const endpoint = timesheet.timesheetId 
        ? `/api/v1/${timesheet.timesheetId}`
        : `/api/v1`;

      console.log('Making request to:', endpoint);
      console.log('Request data:', data);

      const response = await axiosInstance({
        method: timesheet.timesheetId ? 'put' : 'post',
        url: endpoint,
        data: data,
        headers: {
          'Content-Type': 'application/json'
        },
        // Prevent automatic redirect following
        maxRedirects: 0
      });

      await loadTimesheet();
      
      toast({
        title: "Success",
        description: isDraft ? "Timesheet saved as draft" : "Timesheet submitted successfully",
      });
    } catch (error: any) {
      console.error("Save error:", error);
      
      // Check if it's a redirect error
      if (error.response?.status >= 300 && error.response?.status < 400) {
        console.error("Redirect detected:", error.response.headers.location);
        toast({
          variant: "destructive",
          title: "Configuration Error",
          description: "API redirect detected. Please contact administrator.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to save timesheet",
        });
      }
    }
  };

  const handleCalendarSelect = (date: Date | undefined) => {
    if (date) {
      setCurrentWeek(startOfWeek(date, { weekStartsOn: 1 }));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div>
            
            <p className="text-muted-foreground mt-1">
              Week of {format(currentWeek, 'MMM dd, yyyy')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}
              className="h-8 w-8"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  className="w-[180px] justify-start text-left font-normal"
                  disabled={isLoading}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  Select Week
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={currentWeek}
                  onSelect={handleCalendarSelect}
                  disabled={isDateDisabled}
                  initialFocus
                  weekStartsOn={1}
                  defaultMonth={new Date()}
                  fromDate={MIN_DATE}
                  showOutsideDays={false}
                  classNames={{
                    head_cell: "w-10 font-normal text-muted-foreground",
                    cell: cn(
                      "h-10 w-10 text-center text-sm p-0 relative",
                      "[&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20"
                    ),
                    day: cn(
                      "h-10 w-10 p-0 font-normal aria-selected:opacity-100"
                    ),
                    day_today: "bg-accent text-accent-foreground",
                    day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                    day_disabled: "text-muted-foreground opacity-50",
                    day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
                    day_hidden: "invisible",
                    nav_button: cn(
                      "h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100"
                    )
                  }}
                />
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
              className="h-8 w-8"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {canEdit && !isWeekInFuture(currentWeek) && (
          <div className="space-x-2">
            <Button variant="outline" onClick={() => handleSave(true)}>
              Save Draft
            </Button>
            <Button 
              onClick={() => handleSave(false)}
              disabled={!timesheet || calculateTotal() === 0}
            >
              Submit
            </Button>
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg flex items-center gap-2">
              Time Entries
              {timesheet?.status && (
                <span className={`text-sm px-2 py-1 rounded-full ${
                  timesheet.status === 'approved' ? 'bg-green-100 text-green-600' :
                  timesheet.status === 'rejected' ? 'bg-red-100 text-red-600' :
                  timesheet.status === 'submitted' ? 'bg-primary/10 text-primary' :
                  'bg-yellow-100 text-yellow-600'
                }`}>
                  {timesheet.status.toUpperCase()}
                </span>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Weekly Total:</span>
              <span className="text-lg font-semibold">{calculateTotal()} hrs</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-4">
            {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day, index) => {
              const currentDate = addDays(currentWeek, index);
              return (
                <div key={day} className="flex flex-col">
                  <div className="bg-muted/50 rounded-t-lg p-3 border border-b-0">
                    <p className="font-medium text-sm">
                      {format(currentDate, 'EEEE')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {format(currentDate, 'MMM dd, yyyy')}
                    </p>
                  </div>
                  <div className="relative">
                    <input
                      type="number"
                      className={cn(
                        "w-full px-3 py-2 border rounded-b-lg text-right pr-8",
                        "bg-card text-card-foreground border-input",
                        "placeholder:text-muted-foreground",
                        "focus:outline-none focus:ring-2 focus:ring-ring focus:border-input",
                        "transition-all duration-200",
                        "hover:border-ring/50",
                        // Dark mode specific styles
                        "dark:bg-card dark:text-card-foreground dark:border-input",
                        "dark:placeholder:text-muted-foreground",
                        "dark:focus:ring-ring dark:focus:border-input",
                        // Disabled state
                        !canEdit && "opacity-50 cursor-not-allowed bg-muted text-muted-foreground",
                        // Ensure text is always visible
                        "text-foreground dark:text-foreground"
                      )}
                      min="0"
                      max="24"
                      step="0.5"
                      value={timesheet?.[day as keyof Timesheet] ?? ''}
                      onChange={(e) => handleHourChange(day as keyof Timesheet, e.target.value)}
                      disabled={!canEdit || isWeekInFuture(currentWeek)}
                      placeholder="0.0"
                      style={{
                        colorScheme: 'dark light'
                      }}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      hrs
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8">
            <div className="space-y-2">
              <label className="text-sm font-medium">Comments</label>
              <Textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                disabled={!canEdit}
                placeholder="Add any comments about your timesheet here..."
                className={cn(
                  "min-h-[100px] resize-none",
                  !canEdit && "bg-muted cursor-not-allowed"
                )}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WeeklyTimesheet;